/* eslint-disable @typescript-eslint/no-explicit-any */
import axios, { AxiosError, AxiosInstance } from "axios"
import camelcaseKeys from "camelcase-keys"
import decamelizeKeys from "decamelize-keys"
import { type Trello } from "./trello"
import {
  clearDasherToken,
  clearTrelloToken,
  getDasherToken,
  getTrelloToken,
  setDasherToken
} from "./helpers"
import {
  BoardConfig,
  CardTimeEntry,
  ImportQuota,
  NewCardTimeEntry,
  QuickbooksResponse,
  Subscription,
  TimeReport,
  TimeReportInvoiceRequest,
  TimeReportInvoiceResult,
  View
} from "./types"
export interface ApiReqOptions {
  method: string
  body?: unknown
}

interface TrelloRequestData {
  url: string
  method: string
  pup_id: string
  data?: any
}

/**
 * An error Trello produced, as opposed to one this API produced.
 *
 * The proxy answers 502 for these so that its own statuses keep describing
 * itself — a 401 from the Dasher API means the caller's session, never
 * Trello's. Callers still need Trello's status (backing off on 429, treating
 * 404 as an absent feature), so it is presented here under `response`, the
 * shape they already read from an axios failure.
 */
export class TrelloApiError extends Error {
  readonly response: { status: number | null; data: unknown }
  /** The status the proxy itself returned. */
  readonly proxyStatus = 502
  /**
   * Seconds Trello asked the caller to wait, when it said so (a 429 carrying
   * Retry-After). Null means it didn't, not "retry now" — a caller with no
   * number to honour should fall back to its own backoff.
   */
  readonly retryAfter: number | null

  constructor(
    status: number | null,
    data: unknown,
    message: string,
    retryAfter: number | null = null
  ) {
    super(message)
    this.name = "TrelloApiError"
    this.response = { status, data }
    this.retryAfter = retryAfter
  }
}

interface UpstreamErrorBody {
  code?: string
  detail?: string
  trello_status?: number | null
  trello_body?: unknown
  retry_after?: number
}

/**
 * Turn the proxy's upstream envelope back into an error carrying Trello's own
 * status. Anything else — a real 401, a network failure — passes through
 * untouched.
 */
export const unwrapTrelloError = (error: unknown): unknown => {
  const response = (error as AxiosError)?.response
  if (response?.status !== 502) return error

  const body = response.data as UpstreamErrorBody | undefined
  if (body?.code !== "trello_upstream_error") return error

  return new TrelloApiError(
    body.trello_status ?? null,
    body.trello_body ?? null,
    body.detail ?? "Trello could not complete the request.",
    body.retry_after ?? null
  )
}

/**
 * Whether a failure means the Trello connection itself is dead, rather than the
 * action being refused. The API codes these — see `trello_reauth_response` —
 * because Trello answers both with a bare 401 and only one of them is fixed by
 * reconnecting.
 */
export const isTrelloAuthExpired = (error: unknown): boolean =>
  (error as { response?: { data?: { code?: string } } })?.response?.data?.code ===
  "trello_auth_expired"

/**
 * Drop the stored credentials once the API says the Trello connection is dead.
 *
 * Both go: the Trello token because it no longer works, and the Dasher token
 * because it was minted from it and outlives it. That second one is what makes
 * this necessary at all — it never expires, so a pup holding one never returns
 * to /v1/token, which is the only place a revoked Trello token was previously
 * noticed. Without this the pup retries a request that cannot succeed for as
 * long as the user cares to, and re-adding the power-up is the only way out.
 *
 * Done here rather than in each pup so every one of them recovers on the next
 * load; a pup that can do better than a reload still handles the error itself.
 */
const forgetDeadTrelloConnection = async (t: Trello.PowerUp.IFrame, pupId: string) => {
  try {
    await Promise.all([clearTrelloToken(t, pupId), clearDasherToken(t)])
  } catch {
    // Clearing goes through t.set/t.remove, which need a live board context.
    // A teardown mid-flight rejects, and losing the original error to that
    // would be a worse trade than leaving the tokens for the next load.
  }
}

export class ApiClient {
  baseURL: string
  axiosInstance: AxiosInstance
  pupId: string

  #getPupAuthToken = async (t: Trello.PowerUp.IFrame) => {
    const token = await getDasherToken(t)

    if (token) {
      return token
    }

    const trelloUserId = t.getContext().member

    const trelloToken = await getTrelloToken(t, this.pupId)
    const loadedToken = await this.#getAuthToken(t, trelloUserId, trelloToken)

    await setDasherToken(t, loadedToken)
    return loadedToken
  }

  makeTrelloRequest = async (t: Trello.PowerUp.IFrame, data: TrelloRequestData) => {
    try {
      return await this.#pupApiReq(t, `trello`, {
        method: "POST",
        body: data
      })
    } catch (error) {
      throw unwrapTrelloError(error)
    }
  }

  /**
   * Product analytics. Posts to /v1/event; the server stamps the user and (via
   * pup_id) the pup, and stores `eventData` verbatim as JSON. Intended as
   * fire-and-forget by callers. Always sent with the Dasher token, so every
   * event is attributed to the user it names.
   */
  createAnalyticsEvent = async (
    t: Trello.PowerUp.IFrame,
    eventType: string,
    eventData?: Record<string, unknown>
  ) => {
    return await this.#pupApiReq(t, `event`, {
      method: "POST",
      body: { eventType, eventData, pupId: this.pupId }
    })
  }

  #getPupAuthHeader = async (t: Trello.PowerUp.IFrame) => {
    const token = await this.#getPupAuthToken(t)
    return { Authorization: `Token ${token}` }
  }

  #pupApiReq = async (
    t: Trello.PowerUp.IFrame,
    path: string,
    options: ApiReqOptions
  ): Promise<any> => {
    const authHeader = await this.#getPupAuthHeader(t)
    try {
      const response = await this.axiosInstance(path, {
        method: options.method,
        headers: authHeader,
        data: options.body,
        params: options.method === "GET" ? { pup_id: this.pupId } : undefined
      })
      return response?.data ?? null
    } catch (error) {
      if (isTrelloAuthExpired(error)) await forgetDeadTrelloConnection(t, this.pupId)
      throw error
    }
  }

  #getAuthToken = async (
    t: Trello.PowerUp.IFrame,
    trello_user_id: string,
    trello_token: string
  ) => {
    const requestData = {
      trello_user_id,
      pup_id: this.pupId,
      organization_id: t.getContext().organization!,
      trello_token,
      board_id: t.getContext().board,
    }

    try {
      const tokenResponse = await this.axiosInstance.post("token", requestData)
      const authToken = tokenResponse?.data?.token as string
      if (!authToken) throw new Error("No auth token in response")
      return authToken
    } catch (error) {
      if (error instanceof AxiosError && error.response?.status === 400) {
        // We might have saved a bad trello token into local storage. Let's clear it out
        // And make the user re-auth
        clearTrelloToken(t, this.pupId)
      }
      throw error
    }
  }

  postSubscription = async (
    t: Trello.PowerUp.IFrame,
    orgId: string,
    data: Subscription
  ): Promise<Subscription> => {
    return await this.#pupApiReq(t, `organizations/${orgId}/subscriptions/`, {
      method: "POST",
      body: data
    })
  }

  updateSubscription = async (
    t: Trello.PowerUp.IFrame,
    id: string,
    data: Partial<Subscription>
  ): Promise<Subscription> => {
    return await this.#pupApiReq(t, `subscriptions/${id}`, {
      method: "PATCH",
      body: data
    })
  }

  /**
   * The signed-in Trello member as this API knows them.
   *
   * Used to pre-fill the Paddle checkout. Trello doesn't hand a power-up a
   * member's email, but the API recorded it when the Trello token was first
   * verified.
   */
  getCurrentUser = async (t: Trello.PowerUp.IFrame): Promise<{ email?: string | null }> => {
    return await this.#pupApiReq(t, `user`, { method: "GET" })
  }

  getCurrentSubscription = async (
    t: Trello.PowerUp.IFrame,
    orgId: string
  ): Promise<Subscription> => {
    return await this.#pupApiReq(
      t,
      `organizations/${orgId}/currentsubscription/`,
      { method: "GET" }
    )
  }

  /**
   * How many free imports a workspace has left.
   *
   * The import power-up meters free use by imports rather than by days, so it
   * asks for this instead of relying on the shared trial that
   * `getCurrentSubscription` reports.
   */
  getImportQuota = async (
    t: Trello.PowerUp.IFrame,
    orgId: string
  ): Promise<ImportQuota> => {
    return await this.#pupApiReq(
      t,
      `organizations/${orgId}/importquota/`,
      { method: "GET" }
    )
  }

  /** Spend one free import. Returns the workspace's state after the charge. */
  consumeImport = async (
    t: Trello.PowerUp.IFrame,
    orgId: string
  ): Promise<ImportQuota> => {
    return await this.#pupApiReq(
      t,
      `organizations/${orgId}/consumeimport/`,
      { method: "POST", body: { pup_id: this.pupId } }
    )
  }

  getSavedViews = async (t: Trello.PowerUp.IFrame, organizationId: string) => {
    return await this.#pupApiReq(
      t,
      `organizations/${organizationId}/savedviews/`,
      { method: "GET" }
    )
  }

  queryQuickbooksRecords = async (
    t: Trello.PowerUp.IFrame,
    query: string,
  ): Promise<QuickbooksResponse> => {
    return await this.#pupApiReq(t, `quickbooks/query`, {
      method: "POST",
      body: { query, }
    })
  }

  getSavedView = async (t: Trello.PowerUp.IFrame, viewId: string) => {
    return await this.#pupApiReq(t, `savedviews/${viewId}/`, { method: "GET" })
  }

  revokeQuickbooksAuth = async (t: Trello.PowerUp.IFrame) => {
    return await this.#pupApiReq(t, `intuit/revoke`, { method: "GET" })
  }

  getQuickbooksAuthUrl = async (t: Trello.PowerUp.IFrame) => {
    return await this.#pupApiReq(t, `intuit/auth`, { method: "GET" })
  }

  getQuickbooksConnectionStatus = async (t: Trello.PowerUp.IFrame): Promise<{ hasConnection: boolean }> => {
    return await this.#pupApiReq(t, `quickbooks/connection-status`, { method: "GET" })
  }

  postSavedView = async (
    t: Trello.PowerUp.IFrame,
    view: Pick<View, Exclude<keyof View, "id" | "lastUpdated">>
  ) => {
    return await this.#pupApiReq(t, `savedviews/`, {
      method: "POST",
      body: view
    })
  }

  patchSavedView = async (
    t: Trello.PowerUp.IFrame,
    id: string,
    updatedItem: Partial<View>
  ) => {
    return await this.#pupApiReq(t, `savedviews/${id}/`, {
      method: "PATCH",
      body: updatedItem
    })
  }

  deleteSavedView = async (t: Trello.PowerUp.IFrame, viewId: string) => {
    return await this.#pupApiReq(t, `savedviews/${viewId}`, {
      method: "DELETE"
    })
  }

  getBoardConfigs = async (t: Trello.PowerUp.IFrame) => {
    return await this.#pupApiReq(t, `boardconfig/`, { method: "GET" })
  }

  postBoardConfig = async (t: Trello.PowerUp.IFrame, boardConfig: any) => {
    return await this.#pupApiReq(t, `boardconfig/`, {
      method: "POST",
      body: boardConfig
    })
  }

  patchBoardConfig = async (
    t: Trello.PowerUp.IFrame,
    id: string,
    updatedItem: Partial<BoardConfig>
  ) => {
    return await this.#pupApiReq(t, `boardconfig/${id}/`, {
      method: "PATCH",
      body: updatedItem
    })
  }

  getCardCustomer = async (t: Trello.PowerUp.IFrame, trelloCardId: string): Promise<{ customerId: string } | null> => {
    try {
      const data = await this.#pupApiReq(t, `card-customer?trello_card_id=${trelloCardId}`, { method: 'GET' })
      return data ? { customerId: data.qbCustomerId } : null
    } catch (err) {
      if ((err as AxiosError)?.response?.status === 404) return null
      throw err
    }
  }

  setCardCustomer = async (t: Trello.PowerUp.IFrame, trelloCardId: string, qbCustomerId: string): Promise<{ customerId: string }> => {
    const organizationId = t.getContext().organization
    const data = await this.#pupApiReq(t, `card-customer`, {
      method: 'POST',
      body: { trelloCardId, organizationId, qbCustomerId },
    })
    return { customerId: data.qbCustomerId }
  }

  deleteCardCustomer = async (t: Trello.PowerUp.IFrame, trelloCardId: string): Promise<void> => {
    await this.#pupApiReq(t, `card-customer?trello_card_id=${trelloCardId}`, { method: 'DELETE' })
  }

  // ─── Card time tracking ────────────────────────────────────────────────────
  //
  // Time logged in the power-up lives in the Dasher API, not QuickBooks: it is
  // rolled up once a month and pushed across as invoice lines. See
  // `getTimeReport` / `createTimeReportInvoices`.
  //
  // A note on casing in the bodies below: the request interceptor decamelises
  // only the *top level* of a body, so anything nested is sent exactly as
  // written and is spelled snake_case here to match the serializers. Responses
  // are camelised all the way down, so reads are camelCase throughout.

  getCardTimeEntries = async (
    t: Trello.PowerUp.IFrame,
    trelloCardId: string
  ): Promise<CardTimeEntry[]> => {
    return await this.#pupApiReq(t, `card-time-entries/?trello_card_id=${trelloCardId}`, {
      method: "GET"
    })
  }

  createCardTimeEntry = async (
    t: Trello.PowerUp.IFrame,
    entry: NewCardTimeEntry
  ): Promise<CardTimeEntry> => {
    return await this.#pupApiReq(t, `card-time-entries/`, {
      method: "POST",
      body: {
        organization: t.getContext().organization,
        trelloCardId: entry.trelloCardId,
        trelloBoardId: entry.trelloBoardId,
        entryDate: entry.entryDate,
        minutes: entry.minutes,
        // Sent only when the entry was logged as a span. The API derives the
        // minutes from them and ignores the one above, so the two can't drift.
        startTime: entry.startTime ?? null,
        endTime: entry.endTime ?? null,
        note: entry.note ?? ""
      }
    })
  }

  updateCardTimeEntry = async (
    t: Trello.PowerUp.IFrame,
    entryId: number,
    changes: Partial<Pick<NewCardTimeEntry, "entryDate" | "minutes" | "note" | "startTime" | "endTime">>
  ): Promise<CardTimeEntry> => {
    return await this.#pupApiReq(t, `card-time-entries/${entryId}/`, {
      method: "PATCH",
      body: changes
    })
  }

  deleteCardTimeEntry = async (t: Trello.PowerUp.IFrame, entryId: number): Promise<void> => {
    await this.#pupApiReq(t, `card-time-entries/${entryId}/`, { method: "DELETE" })
  }

  /** Every unbilled time entry on a board. */
  getTimeReport = async (t: Trello.PowerUp.IFrame, boardId: string): Promise<TimeReport> => {
    const params = new URLSearchParams({ board_id: boardId })
    return await this.#pupApiReq(t, `card-time-entries/report/?${params}`, { method: "GET" })
  }

  /**
   * Bill tracked time: one QuickBooks invoice for the named customer, with the
   * lines the draft screen composed. Each line names the entries it covers and
   * the server sums their durations, so `lines` only says how to group and
   * price the work, never how long it took.
   */
  createTimeReportInvoices = async (
    t: Trello.PowerUp.IFrame,
    request: TimeReportInvoiceRequest
  ): Promise<TimeReportInvoiceResult> => {
    return await this.#pupApiReq(t, `card-time-entries/invoice/`, {
      method: "POST",
      body: {
        boardId: request.boardId,
        qbCustomerId: request.qbCustomerId,
        txnDate: request.txnDate ?? null,
        lines: request.lines.map((line) => ({
          entry_ids: line.entryIds,
          description: line.description,
          item_id: line.itemId,
          item_name: line.itemName,
          unit_price: line.unitPrice
        }))
      }
    })
  }

  /**
   * A board's Gantt payload, assembled server-side in one round trip.
   *
   * The Trello halves (board, cards, lists, labels, members) are Trello's own
   * JSON passed through, so their keys are Trello's — already camelCase, and
   * unchanged by the response interceptor. `dependencies` is ours, stored as
   * JSON against the board.
   */
  getGanttData = async (t: Trello.PowerUp.IFrame, boardId: string) => {
    return await this.#pupApiReq(t, `gantt-data/${boardId}/`, { method: "GET" })
  }

  /**
   * Replace the board's dependency list.
   *
   * The array is stored verbatim as JSON, so its `fromId`/`toId` keys have to
   * reach the server intact. They do: the request interceptor decamelizes only
   * top-level keys, and these are nested under `dependencies`.
   */
  patchGanttDependencies = async (
    t: Trello.PowerUp.IFrame,
    boardId: string,
    dependencies: { fromId: string; toId: string }[]
  ) => {
    return await this.#pupApiReq(t, `gantt/${boardId}/`, {
      method: "PATCH",
      body: { dependencies }
    })
  }

  /**
   * The board's public share link, or a 404 when it has none.
   *
   * The 404 is the API's own, not Trello's, so it stays a plain 404 rather than
   * arriving in the upstream envelope — callers that read it as "no link yet"
   * can go on doing so.
   */
  getShareLink = async (t: Trello.PowerUp.IFrame, boardId: string) => {
    return await this.#pupApiReq(t, `share-links?board_id=${boardId}`, {
      method: "GET"
    })
  }

  createShareLink = async (t: Trello.PowerUp.IFrame, boardId: string) => {
    return await this.#pupApiReq(t, `share-links`, {
      method: "POST",
      body: { boardId, pupId: this.pupId }
    })
  }

  deleteShareLink = async (t: Trello.PowerUp.IFrame, boardId: string) => {
    // Spelled out rather than left to #pupApiReq, which only attaches pup_id to
    // GETs.
    return await this.#pupApiReq(
      t,
      `share-links?board_id=${boardId}&pup_id=${this.pupId}`,
      { method: "DELETE" }
    )
  }

  sendTrelloTokenToDasher = async (t: Trello.PowerUp.IFrame, token: string) => {
    // Even if they have a BD token, we need to make sure BD has their trello_token
    // They might already have it if this is a re-auth
    const trelloUserId = t.getContext().member
    await this.#getAuthToken(t, trelloUserId, token)
  }

  constructor(baseURL: string, pupId: string) {
    this.baseURL = baseURL
    this.pupId = pupId
    this.axiosInstance = axios.create({
      timeout: 30000,
      baseURL,
      headers: {
        "Content-Type": "application/json"
      }
    })
    this.axiosInstance.interceptors.response.use(
      function (response) {
        if (response.data) {
          response.data = camelcaseKeys(response.data, { deep: true })
        }
        return response
      },
      async function (error) {
        return await Promise.reject(error)
      }
    )
    this.axiosInstance.interceptors.request.use(
      function (config) {
        if (config.method === "patch" || config.method === "post") {
          config.data = decamelizeKeys(config.data)
        }
        return config
      },
      async function (error) {
        return await Promise.reject(error)
      }
    )
  }
}
